import * as React from 'react'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { useSummaryById } from '@renderer/chat/hooks'
import { useSummaryContext } from '../hooks/use-summary-context'
import { useSummaryEdit } from '../hooks/use-summary-edit'

export interface SummaryEditorProps extends Omit<React.ComponentProps<'div'>, 'onChange'> {
  editorClassName?: string
  autoFocus?: boolean
}

export function SummaryEditor({ className, editorClassName, autoFocus = true, ...props }: SummaryEditorProps) {
  const { summary } = useSummaryContext()
  const { data: fullSummary } = useSummaryById(summary.id)
  const { edited, setEdited, autoSaveRef, onSaveEdits } = useSummaryEdit()

  const value = useMemo(() => {
    const rawMarkdown = edited ?? (fullSummary?.summary || '')
    // Clean escaped brackets and HTML entities in checkbox syntax before editing
    // Handles: \[ ], \[x], [&#x20;] → - [ ], - [x]
    return rawMarkdown.replace(/^(\s*)\\?\[( |x|X|&#x20;)\]\\?\s*/gm, (match, indent, checkbox) => {
      const cleanCheckbox = checkbox === '&#x20;' ? ' ' : checkbox
      return `${indent}- [${cleanCheckbox}] `
    })
  }, [edited, fullSummary])

  const handleChange = React.useCallback(
    (val: string) => {
      setEdited(val)

      // Clear existing timeout
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current)
      }

      // Set new debounced auto-save (2 second delay)
      autoSaveRef.current = setTimeout(() => {
        onSaveEdits(val)
      }, 2000)
    },
    [setEdited, autoSaveRef, onSaveEdits],
  )

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    // Stop propagation to prevent ChatInput's focus-hijacking logic from interfering
    e.stopPropagation()
  }, [])

  return (
    <>
      <style>
        {`
          [class*="_code_"] {
            background-color: transparent !important;
          }
          [class*="_contentEditable_"] {
            padding: 0 !important;
          }
        `}
      </style>
      <div
        style={
          {
            // MDXEditor CSS variables for dark theme
            '--accentBase': 'rgba(255, 255, 255, 0.02)',
            '--accentBgSubtle': 'rgba(255, 255, 255, 0.05)',
            '--accentBg': 'rgba(255, 255, 255, 0.1)',
            '--accentBgHover': 'rgba(255, 255, 255, 0.15)',
            '--accentBgActive': 'rgba(255, 255, 255, 0.2)',
            '--accentLine': 'rgba(255, 255, 255, 0.1)',
            '--accentBorder': 'rgba(255, 255, 255, 0.1)',
            '--accentBorderHover': 'rgba(255, 255, 255, 0.2)',
            '--accentSolid': 'rgba(255, 255, 255, 0.6)',
            '--accentSolidHover': 'rgba(255, 255, 255, 0.7)',
            '--accentText': 'rgba(255, 255, 255, 0.8)',
            '--accentTextContrast': 'rgba(255, 255, 255, 0.9)',
            '--baseBase': 'rgba(255, 255, 255, 0.02)',
            '--baseBgSubtle': 'rgba(255, 255, 255, 0.05)',
            '--baseBg': 'rgba(255, 255, 255, 0.1)',
            '--baseBgHover': 'rgba(255, 255, 255, 0.15)',
            '--baseBgActive': 'rgba(255, 255, 255, 0.2)',
            '--baseLine': 'rgba(255, 255, 255, 0.1)',
            '--baseBorder': 'rgba(255, 255, 255, 0.1)',
            '--baseBorderHover': 'rgba(255, 255, 255, 0.2)',
            '--baseSolid': 'rgba(255, 255, 255, 0.6)',
            '--baseSolidHover': 'rgba(255, 255, 255, 0.7)',
            '--baseText': 'rgba(255, 255, 255, 0.8)',
            '--baseTextContrast': 'rgba(255, 255, 255, 0.9)',
          } as React.CSSProperties
        }
        className={twMerge(
          // Wrapper styles
          '!m-0 !p-0',
          // Base editor styles
          '[&_.mdxeditor]:!m-0 [&_.mdxeditor]:min-h-0 [&_.mdxeditor]:!border-0 [&_.mdxeditor]:!bg-transparent [&_.mdxeditor]:!p-0',
          '[&_.mdxeditor-root-contenteditable]:!p-0 [&_.mdxeditor-root-contenteditable]:!text-sm [&_.mdxeditor-root-contenteditable]:!leading-relaxed',
          // Paragraph styles
          '[&_.mdxeditor-root-contenteditable_p]:!text-light/40 [&_.mdxeditor-root-contenteditable_p]:!my-0.5 [&_.mdxeditor-root-contenteditable_p]:!font-[350]',
          // Heading styles - must use color instead of text-light for specificity
          '[&_.mdxeditor-root-contenteditable_h1]:!mt-1.5 [&_.mdxeditor-root-contenteditable_h1]:!text-[15px] [&_.mdxeditor-root-contenteditable_h1]:!font-medium [&_.mdxeditor-root-contenteditable_h1]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h2]:!mt-1.5 [&_.mdxeditor-root-contenteditable_h2]:!text-sm [&_.mdxeditor-root-contenteditable_h2]:!font-medium [&_.mdxeditor-root-contenteditable_h2]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h3]:!mt-1.5 [&_.mdxeditor-root-contenteditable_h3]:!text-sm [&_.mdxeditor-root-contenteditable_h3]:!font-medium [&_.mdxeditor-root-contenteditable_h3]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h4]:!mt-1.5 [&_.mdxeditor-root-contenteditable_h4]:!text-sm [&_.mdxeditor-root-contenteditable_h4]:!font-medium [&_.mdxeditor-root-contenteditable_h4]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h5]:!mt-1.5 [&_.mdxeditor-root-contenteditable_h5]:!text-sm [&_.mdxeditor-root-contenteditable_h5]:!font-medium [&_.mdxeditor-root-contenteditable_h5]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h6]:!mt-1.5 [&_.mdxeditor-root-contenteditable_h6]:!text-sm [&_.mdxeditor-root-contenteditable_h6]:!font-medium [&_.mdxeditor-root-contenteditable_h6]:!text-[rgba(255,255,255,0.8)]',
          // Heading span elements need explicit color too
          '[&_.mdxeditor-root-contenteditable_h1_*]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h2_*]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h3_*]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h4_*]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h5_*]:!text-[rgba(255,255,255,0.8)]',
          '[&_.mdxeditor-root-contenteditable_h6_*]:!text-[rgba(255,255,255,0.8)]',
          // Default text color - applied after headings
          '[&_.mdxeditor-root-contenteditable]:!text-light/40',
          // Link styles
          '[&_.mdxeditor-root-contenteditable_a]:!text-light/60 [&_.mdxeditor-root-contenteditable_a]:hover:!text-light/80 [&_.mdxeditor-root-contenteditable_a]:!underline',
          // List styles
          '[&_.mdxeditor-root-contenteditable_ul]:!text-light/40 [&_.mdxeditor-root-contenteditable_ul]:!my-1 [&_.mdxeditor-root-contenteditable_ul]:!p-0',
          '[&_.mdxeditor-root-contenteditable_ol]:!text-light/40 [&_.mdxeditor-root-contenteditable_ol]:!my-1',
          '[&_.mdxeditor-root-contenteditable_li]:!text-light/40 [&_.mdxeditor-root-contenteditable_li]:!my-2 [&_.mdxeditor-root-contenteditable_li]:!ms-5',
          '[&_.mdxeditor-root-contenteditable_li_*]:!text-light/40',
          // Checklist styles
          '[&_li[role="checkbox"]]:!relative [&_li[role="checkbox"]]:!my-2 [&_li[role="checkbox"]]:!ms-1 [&_li[role="checkbox"]]:!pl-[26px]',
          '[&_li[role="checkbox"]::before]:!border-light/5 [&_li[role="checkbox"]::before]:!bg-light/5 [&_li[role="checkbox"]::before]:!absolute [&_li[role="checkbox"]::before]:!left-0 [&_li[role="checkbox"]::before]:!top-0 [&_li[role="checkbox"]::before]:!h-5 [&_li[role="checkbox"]::before]:!w-5 [&_li[role="checkbox"]::before]:!rounded [&_li[role="checkbox"]::before]:!border [&_li[role="checkbox"]::before]:!content-[""]',
          '[&_li[role="checkbox"][aria-checked="true"]::after]:!absolute [&_li[role="checkbox"][aria-checked="true"]::after]:!left-[8px] [&_li[role="checkbox"][aria-checked="true"]::after]:!top-[5px]',
          '[&_li[role="checkbox"][aria-checked="true"]]:!line-through [&_li[role="checkbox"][aria-checked="true"]]:!opacity-60',
          // Inline code styles
          '[&_.mdxeditor-root-contenteditable_code]:!bg-light/5 [&_.mdxeditor-root-contenteditable_code]:!rounded-md [&_.mdxeditor-root-contenteditable_code]:!p-0.5 [&_.mdxeditor-root-contenteditable_code]:!px-1 [&_.mdxeditor-root-contenteditable_code]:!text-[12px]',
          // Code block styles
          '[&_.mdxeditor-root-contenteditable_pre]:!bg-light/5 [&_.mdxeditor-root-contenteditable_pre]:!my-2 [&_.mdxeditor-root-contenteditable_pre]:!overflow-x-auto [&_.mdxeditor-root-contenteditable_pre]:!rounded-lg [&_.mdxeditor-root-contenteditable_pre]:!p-3',
          '[&_.mdxeditor-root-contenteditable_pre_code]:!bg-transparent [&_.mdxeditor-root-contenteditable_pre_code]:!p-0 [&_.mdxeditor-root-contenteditable_pre_code]:!text-[13px] [&_.mdxeditor-root-contenteditable_pre_code]:!leading-relaxed',
          // CodeMirror styles
          '[&_.cm-editor]:!bg-light/5 [&_.cm-editor]:!my-2 [&_.cm-editor]:!rounded-lg',
          '[&_.cm-scroller]:!text-[13px] [&_.cm-scroller]:!leading-relaxed',
          '[&_.cm-content]:!text-light/40',
          '[&_.cm-line]:!text-light/40',
          // Toolbar styles - hide toolbar completely
          '[&_.mdxeditor-toolbar]:!hidden',
          // Strong/bold styles
          '[&_.mdxeditor-root-contenteditable_strong]:!text-light/60 [&_.mdxeditor-root-contenteditable_strong]:!font-medium',
          className,
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <MDXEditor
          markdown={value}
          onChange={handleChange}
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            markdownShortcutPlugin(),
            linkPlugin(),
            codeBlockPlugin({ defaultCodeBlockLanguage: 'txt' }),
            codeMirrorPlugin({
              codeBlockLanguages: {
                txt: 'Plain Text',
                js: 'JavaScript',
                ts: 'TypeScript',
                tsx: 'TypeScript (JSX)',
                jsx: 'JavaScript (JSX)',
                css: 'CSS',
                html: 'HTML',
                json: 'JSON',
                md: 'Markdown',
                bash: 'Bash',
                sh: 'Shell',
                python: 'Python',
                py: 'Python',
                go: 'Go',
                rust: 'Rust',
                java: 'Java',
                c: 'C',
                cpp: 'C++',
                sql: 'SQL',
              },
            }),
          ]}
          contentEditableClassName={editorClassName}
          autoFocus={autoFocus}
        />
      </div>
    </>
  )
}

SummaryEditor.displayName = 'SummaryEditor'
